import { CARGO_STACK_LIMIT } from "@frontier/content/cargo-stacks";
import { useEffect, useId, useRef } from "react";
import { CargoHold, cargoView, type CargoHoldProps } from "./CargoHold";
import { pressHandlers } from "./press-handlers";
import "./cargo-control.css";

interface Props extends CargoHoldProps {
  open: boolean;
  disabled: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CargoControl({ open, disabled, onOpenChange, ...hold }: Props) {
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLElement>(null);
  const change = useRef(onOpenChange);
  change.current = onOpenChange;
  const id = useId();
  const { contents, used, capacity, units, awaiting } = cargoView(hold.pilot, hold.cargo);
  const available = !disabled && !hold.pilot.dead && !hold.pilot.docked;
  const expanded = open && available;

  useEffect(() => {
    if (open && !available) change.current(false);
  }, [open, available]);
  useEffect(() => {
    if (!expanded) return;
    panel.current?.focus({ preventScroll: true });
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        change.current(false);
      }
      if (event.key !== "Tab") return;
      const buttons = panel.current?.querySelectorAll<HTMLButtonElement>(
        "button:not(:disabled)",
      );
      const first = buttons?.[0];
      const last = buttons?.[buttons.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === panel.current)) {
        event.preventDefault();
        last?.focus();
      } else if (
        !event.shiftKey &&
        (active === last || active === panel.current)
      ) {
        event.preventDefault();
        first?.focus();
      }
    };
    const focusin = () => {
      if (!panel.current?.contains(document.activeElement))
        panel.current?.focus();
    };
    document.addEventListener("keydown", keydown, true);
    document.addEventListener("focusin", focusin);
    return () => {
      document.removeEventListener("keydown", keydown, true);
      document.removeEventListener("focusin", focusin);
      if (trigger.current?.isConnected && !trigger.current.disabled)
        trigger.current.focus({ preventScroll: true });
    };
  }, [expanded]);
  useEffect(() => {
    if (expanded && !panel.current?.contains(document.activeElement))
      panel.current?.focus({ preventScroll: true });
  }, [expanded, hold.pilot.pending, contents.length]);

  return (
    <div className="cargo-control">
      <button
        ref={trigger}
        className="cargo-toggle"
        type="button"
        aria-label={`Трюм: ${awaiting ? "подтверждаем состав" : `${used} из ${capacity} ячеек`}, ${units} ед. груза`}
        title={`Трюм · ${units} ед. груза · ${awaiting ? "…" : used}/${capacity} ячеек`}
        aria-haspopup="dialog"
        aria-expanded={expanded}
        aria-controls={expanded ? id : undefined}
        disabled={!available}
        onKeyDown={(event) => {
          if (event.key === " " || event.key === "Enter")
            event.stopPropagation();
        }}
        {...pressHandlers(() => {
          if (available) onOpenChange(!expanded);
        })}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
        >
          <path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Zm0 9 8-4.5M12 12 4 7.5M12 12v9M8 5.25l8 4.5v4.5" />
        </svg>
        <span aria-hidden="true">
          {awaiting ? "…" : used}
          <small>/{capacity}</small>
        </span>
      </button>
      {expanded && (
        <div
          className="cargo-popover-layer"
          onPointerDown={(event) => {
            if (event.target === event.currentTarget && event.button === 0) {
              event.preventDefault();
              onOpenChange(false);
            }
          }}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <section
            ref={panel}
            id={id}
            className="cargo-popover"
            role="dialog"
            aria-modal="true"
            aria-label="Трюм"
            tabIndex={-1}
          >
            <button
              className="cargo-close"
              type="button"
              aria-label="Закрыть трюм"
              {...pressHandlers(() => onOpenChange(false))}
            >
              ×
            </button>
            <CargoHold {...hold} />
            <p className="cargo-stack-hint">
              До {CARGO_STACK_LIMIT} контейнеров одного вида в ячейке
            </p>
          </section>
        </div>
      )}
    </div>
  );
}
